/**
 * An article's price under each tariff.
 *
 * The tariffs are the same for every article — they are managed on the pricing
 * screen — and the amount under each is the article's own. One row per tariff,
 * a fixed amount to type or change, saved on its own. A tariff with no amount
 * yet shows what it would charge anyway (its own rules, or the base price), so
 * the row is never blank and never misleading.
 */

import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAction, useResource } from "@hardauto/core-data";
import { money } from "@hardauto/core-i18n";
import { Button, Card, Input, useToast } from "@hardauto/core-ui";

import type { ItemPrice, ReferentialApi } from "../api";
import { NAMESPACE } from "../i18n";

export interface ArticlePricesCardProps {
  api: ReferentialApi;
  articleId: string;
  mayWrite: boolean;
}

/** `12,5` and `12.50` are the same amount; anything else is not one. */
function normalise(text: string): string | null {
  const cleaned = text.replace(/\s/g, "").replace(",", ".");
  return /^\d+(\.\d{1,2})?$/.test(cleaned) ? cleaned : null;
}

export function ArticlePricesCard({ api, articleId, mayWrite }: ArticlePricesCardProps) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const toast = useToast();
  const action = useAction();
  const fetcher = useCallback(() => api.itemPrices(articleId), [api, articleId]);
  const data = useResource(fetcher);
  // What was typed and not yet saved, by tariff.
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  function save(row: ItemPrice, price: string | null) {
    void action.run(row.scale_code, async () => {
      try {
        await api.setItemPrice(articleId, row.scale_code, price);
        setDrafts((current) => {
          const next = { ...current };
          delete next[row.scale_code];
          return next;
        });
        data.reload();
      } catch {
        toast.show(t("error.title"), { tone: "danger" });
      }
    });
  }

  const rows = data.data?.items ?? [];
  return (
    <Card title={t("article.prices.title")} compactTitle>
      {rows.length === 0 ? (
        <p className="hardauto-cell__muted">{t("article.prices.none")}</p>
      ) : (
        <table className="hardauto-prices">
          <caption className="hardauto-visually-hidden">{t("article.prices.title")}</caption>
          <thead>
            <tr>
              <th scope="col">{t("article.prices.tariff")}</th>
              <th scope="col">{t("article.prices.amount")}</th>
              <th scope="col">{t("article.prices.applied")}</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const draft = drafts[row.scale_code];
              const current = row.fixed_price ?? "";
              const typed = draft ?? current;
              const parsed = normalise(typed);
              const changed = draft !== undefined && parsed !== null && parsed !== row.fixed_price;
              return (
                <tr key={row.scale_code}>
                  <th scope="row">{row.label}</th>
                  <td>
                    <Input
                      aria-label={row.label}
                      inputMode="decimal"
                      value={typed}
                      disabled={!mayWrite}
                      aria-invalid={draft !== undefined && typed !== "" && parsed === null}
                      onChange={(event) =>
                        setDrafts((now) => ({ ...now, [row.scale_code]: event.target.value }))
                      }
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && changed) save(row, parsed);
                      }}
                    />
                  </td>
                  <td>{money(row.unit_price, i18n.language)}</td>
                  <td>
                    {mayWrite ? (
                      <>
                        <Button
                          variant="primary"
                          disabled={!changed}
                          busy={action.isBusy(row.scale_code)}
                          busyLabel={t("article.prices.save")}
                          onClick={() => save(row, parsed)}
                        >
                          {t("article.prices.save")}
                        </Button>{" "}
                        {row.fixed_price !== null ? (
                          <Button
                            disabled={action.isBusy(row.scale_code)}
                            onClick={() => save(row, null)}
                          >
                            {t("article.prices.remove")}
                          </Button>
                        ) : null}
                      </>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Card>
  );
}
